/**
 * Operation Mode — real-time infiltration spread (Plague-style).
 * Pure & deterministic (no Math.random): each region's infiltration 0..100 is
 * advanced one tick from its own logistic growth, adjacent infected pressure
 * (land vector) and non-adjacent air/sea seeding, scaled by climate match and
 * surveillance. Everything is unit-testable.
 */

import { REGIONS, getRegion } from '../data/regions.js';
import type { EvoEffects } from '../data/evolution.js';

/** Per-tick self-growth scaler for an already-infiltrated region. */
export const BASE_SELF = 0.9;
/** Constant creep so an infiltrated region keeps climbing to 100 (never stalls). */
export const SELF_CREEP = 0.18;
/** Logistic weight (peaks mid-infiltration). */
export const SELF_LOG_K = 3.4;
/** Per-tick seeding scaler from neighbours / air / sea into a region. */
export const BASE_SEED = 0.55;
/** Surveillance penalty per level above 1 (higher surveillance = slower). */
export const SURV_PENALTY = 0.05;
/** Floor for the surveillance multiplier. */
export const SURV_FLOOR = 0.65;
/** Infiltration at/above this counts as fully dominated (display + win check). */
export const DOMINATED_AT = 99.5;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Mean infiltration across all regions (0..100). */
export function meanInfection(infection: Record<string, number>): number {
  let sum = 0;
  for (const r of REGIONS) sum += infection[r.id] ?? 0;
  return sum / REGIONS.length;
}

/** Environment multiplier for a region: climate affinity x surveillance penalty. */
export function envMatch(regionId: string, effects: EvoEffects): number {
  const region = getRegion(regionId);
  if (!region) return 1;
  const climBonus = effects.climate[region.climate] ?? 0;
  const climMul = 1 + climBonus;
  const survMul = clamp(1 - (region.surveillance - 1) * SURV_PENALTY, SURV_FLOOR, 1);
  return climMul * survMul;
}

/** Growth for a single region given current map + aggregated effects. */
export function regionGrowth(
  regionId: string,
  infection: Record<string, number>,
  effects: EvoEffects,
  mean: number,
): number {
  const region = getRegion(regionId);
  if (!region) return 0;
  const inf = infection[regionId] ?? 0;
  const headroom = 1 - inf / 100;
  if (headroom <= 0) return 0;

  const m = envMatch(regionId, effects) * effects.spreadMul;

  // Self logistic growth (only once seeded).
  const frac = inf / 100;
  const self = inf > 0 ? BASE_SELF * m * (SELF_CREEP + SELF_LOG_K * frac * (1 - frac)) : 0;

  // Neighbour bleed (land vector) — sum of adjacent infiltration as region-units.
  let neighbour = 0;
  for (const adjId of region.adjacency) neighbour += (infection[adjId] ?? 0) / 100;
  const land = neighbour * effects.landMul;

  // Non-adjacent air/sea seeding scaled by how far along the whole world is.
  const global = (effects.airVector + effects.seaVector) * (mean / 100);

  const seed = (land + global) * BASE_SEED * m;

  return (self + seed) * headroom;
}

/**
 * Advance the whole infiltration map one tick. Returns a NEW map (immutable).
 * Deterministic: identical inputs always produce identical output.
 */
export function stepInfection(
  infection: Record<string, number>,
  effects: EvoEffects,
): Record<string, number> {
  const mean = meanInfection(infection);
  const next: Record<string, number> = {};
  for (const region of REGIONS) {
    const inf = infection[region.id] ?? 0;
    let v = clamp(inf + regionGrowth(region.id, infection, effects, mean), 0, 100);
    if (v >= DOMINATED_AT) v = 100;
    next[region.id] = v;
  }
  return next;
}

/** Sum of all infiltration as "region equivalents" (0..REGIONS.length). */
export function totalInfectionUnits(infection: Record<string, number>): number {
  let sum = 0;
  for (const r of REGIONS) sum += (infection[r.id] ?? 0) / 100;
  return sum;
}

/** Count of fully dominated regions. */
export function dominatedCount(infection: Record<string, number>): number {
  let n = 0;
  for (const r of REGIONS) if ((infection[r.id] ?? 0) >= 100) n++;
  return n;
}

/** Win: every region fully dominated. */
export function allDominated(infection: Record<string, number>): boolean {
  return dominatedCount(infection) === REGIONS.length;
}
